import { useEffect, useState, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { giftCardsApi } from '../../services/api';
import { cn } from '@/lib/utils';
import { 
  Plus, 
  Minus, 
  Edit, 
  Calendar, 
  Trash2, 
  CreditCard,
  ArrowRightLeft,
  Download,
  ShoppingCart
} from 'lucide-react';

interface GiftCardTransactionsProps {
  cardId: string;
  cardName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface HistoryItem {
  id: string;
  type: 'history' | 'transaction';
  action: string;
  description: string | null;
  oldValue: string | null;
  newValue: string | null;
  saleId: string | null;
  createdAt: string;
}

export function GiftCardTransactions({ cardId, cardName, isOpen, onClose }: GiftCardTransactionsProps) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && cardId) {
      loadHistory();
    }
  }, [isOpen, cardId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await giftCardsApi.getHistory(cardId);
      setHistory(data.history || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при загрузке истории');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATED':
        return <Plus className="h-4 w-4" />;
      case 'SOLD':
        return <ShoppingCart className="h-4 w-4" />;
      case 'BALANCE_UPDATED':
        return <Edit className="h-4 w-4" />;
      case 'STATUS_UPDATED':
        return <ArrowRightLeft className="h-4 w-4" />;
      case 'EXPIRE_DATE_UPDATED':
        return <Calendar className="h-4 w-4" />;
      case 'TRANSACTION_REDEEM':
        return <Minus className="h-4 w-4" />;
      case 'TRANSACTION_REFUND':
        return <Plus className="h-4 w-4" />;
      case 'DELETED':
        return <Trash2 className="h-4 w-4" />;
      case 'DOWNLOADED':
        return <Download className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'CREATED':
        return 'Создание';
      case 'SOLD':
        return 'Продажа';
      case 'BALANCE_UPDATED':
        return 'Изменение баланса';
      case 'STATUS_UPDATED':
        return 'Изменение статуса';
      case 'EXPIRE_DATE_UPDATED':
        return 'Изменение срока действия';
      case 'TRANSACTION_REDEEM':
        return 'Использование';
      case 'TRANSACTION_REFUND':
        return 'Возврат';
      case 'DELETED':
        return 'Удаление';
      case 'DOWNLOADED':
        return 'Скачивание';
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'SOLD':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'BALANCE_UPDATED':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'STATUS_UPDATED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'EXPIRE_DATE_UPDATED':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'TRANSACTION_REDEEM':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'TRANSACTION_REFUND':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DELETED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'DOWNLOADED':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'CAN_BE_USED': 'Не использован',
      'PARTIALLY_REDEEMED': 'Частично использован',
      'EXPIRED': 'Истек срок действия',
      'REDEEMED': 'Полностью использован',
      'INVALID': 'Недействителен',
    };
    return statusMap[status] || status;
  };

  const formatValue = (value: string | null, action: string, oldValue?: string | null): string | ReactNode => {
    // Для скачивания и удаления всегда показываем "Успешно"
    if (action === 'DOWNLOADED' || action === 'DELETED') {
      return <span className="text-green-600 dark:text-green-400 font-medium">Успешно</span>;
    }
    
    if (!value) return '-';
    
    // Для транзакций показываем сумму использования/возврата и изменение баланса в одну строку
    if (action === 'TRANSACTION_REDEEM' || action === 'TRANSACTION_REFUND') {
      // Вычисляем сумму транзакции из разницы балансов
      // Формат: "1000.00 ₽" или просто число
      const parseBalance = (balanceStr: string): number => {
        const cleaned = balanceStr.replace(/[₽\s]/g, '').trim();
        return parseFloat(cleaned) || 0;
      };
      
      const oldBalance = oldValue ? parseBalance(oldValue) : 0;
      const newBalance = parseBalance(value);
      const transactionAmount = Math.abs(newBalance - oldBalance);
      
      // Для использования добавляем знак минуса, для возврата - плюс
      const sign = action === 'TRANSACTION_REDEEM' ? '-' : '+';
      
      // Показываем в одну строку: "- 500.00 ₽ (500.00 ₽ → 0.00 ₽)"
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">
            {sign} {transactionAmount.toFixed(2)} ₽
          </span>
          {oldValue && (
            <span className="text-muted-foreground">
              (
              <span className="line-through">{oldValue}</span>
              <span className="mx-1">→</span>
              <span className="font-medium">{value}</span>
              )
            </span>
          )}
        </div>
      );
    }
    
    // Для изменения баланса показываем в формате транзакций
    if (action === 'BALANCE_UPDATED') {
      // Вычисляем сумму изменения из разницы балансов
      const parseBalance = (balanceStr: string): number => {
        const cleaned = balanceStr.replace(/[₽\s]/g, '').trim();
        return parseFloat(cleaned) || 0;
      };
      
      const oldBalance = oldValue ? parseBalance(oldValue) : 0;
      const newBalance = parseBalance(value);
      const changeAmount = newBalance - oldBalance;
      
      // Определяем знак: "-" если уменьшился, "+" если увеличился
      const sign = changeAmount < 0 ? '-' : '+';
      
      // Показываем в одну строку: "- 3290.00 ₽ (5000.00 ₽ → 1710.00 ₽)"
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">
            {sign} {Math.abs(changeAmount).toFixed(2)} ₽
          </span>
          {oldValue && (
            <span className="text-muted-foreground">
              (
              <span className="line-through">{oldValue}</span>
              <span className="mx-1">→</span>
              <span className="font-medium">{value}</span>
              )
            </span>
          )}
        </div>
      );
    }
    
    // Для статусов показываем русский перевод
    if (action === 'STATUS_UPDATED') {
      return getStatusLabel(value);
    }
    
    return value;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[1400px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>История сертификата: {cardName}</DialogTitle>
          <DialogDescription>
            Полная история всех действий с сертификатом
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="text-center py-10 text-muted-foreground">Загрузка...</div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">История не найдена</div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Действие</TableHead>
                  <TableHead>Изменения</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                        getActionColor(item.action)
                      )}>
                        {getActionIcon(item.action)}
                        {getActionLabel(item.action)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {item.oldValue && item.newValue ? (
                        // Для транзакций и изменения баланса показываем специальный формат
                        item.action === 'TRANSACTION_REDEEM' || 
                        item.action === 'TRANSACTION_REFUND' || 
                        item.action === 'BALANCE_UPDATED' ? (
                          formatValue(item.newValue, item.action, item.oldValue)
                        ) : (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground line-through">
                              {formatValue(item.oldValue, item.action)}
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-medium">
                              {formatValue(item.newValue, item.action)}
                            </span>
                          </div>
                        )
                      ) : item.action === 'DOWNLOADED' || item.action === 'DELETED' ? (
                        // Для скачивания и удаления показываем "Успешно"
                        formatValue(null, item.action)
                      ) : item.newValue ? (
                        <span className="font-medium">
                          {formatValue(item.newValue, item.action)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
